import { AlertTriangle } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Button } from './Button';

export interface ErrorStateProps {
  /** Title rendered below the icon. Defaults to a generic PT-BR message. */
  title?: ReactNode;
  /** Optional descriptive paragraph (e.g. error message). */
  description?: ReactNode;
  /** Callback for the "Tentar novamente" button. When omitted, no button renders. */
  onRetry?: () => void;
  /** Override the retry button label. */
  retryLabel?: string;
  /** Extra classes for the outer wrapper. */
  className?: string;
}

/**
 * Generic error placeholder for failed data loads.
 *
 * Renders a danger-tinted circular icon, a title, an optional description and
 * an optional retry button (only when `onRetry` is provided).
 */
export function ErrorState({
  title = 'Algo deu errado',
  description,
  onRetry,
  retryLabel = 'Tentar novamente',
  className,
}: ErrorStateProps): ReactElement {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-card border border-danger/40 ' +
          'bg-danger/5 px-6 py-12 text-center',
        className,
      )}
      role="alert"
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-danger/15 text-danger">
        <AlertTriangle size={24} aria-hidden />
      </span>
      <h3 className="text-base font-semibold text-white">{title}</h3>
      {description ? (
        <p className="max-w-md text-sm text-muted">{description}</p>
      ) : null}
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-2">
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}
